/**
 * ログイン画面。
 * - 管理者・閲覧者は **Googleアカウント**でログインする(2026-09-15 発注者判断)。初回は管理者の招待が必要
 * - メールアドレス＋パスワードの欄は、ローカルのエミュレータでの検証用にだけ出す
 * - スタッフ(operator)のログイン方式は保留(P-12)
 */
import { BUILD_LABEL } from '@core/lib/buildInfo';
import { useState, type FormEvent } from 'react';
import { FirebaseError } from 'firebase/app';
import { useAuth } from './AuthProvider';

const useEmulator = import.meta.env.VITE_USE_EMULATOR === 'true';
/**
 * メールアドレスでのログイン欄を出すか。手元の検証環境か、URL に `?login=email` が付いたときだけ。
 * スタッフ(operator)のログイン方式は未確定(P-12)なので、**ふだんは出さない**(2026-09-20 発注者指示。D-109)
 */
const emailLoginAllowed = () => useEmulator || new URLSearchParams(window.location.search).get('login') === 'email';

function messageFor(e: unknown): string {
  if (e instanceof FirebaseError) {
    switch (e.code) {
      case 'auth/invalid-email':
        return 'メールアドレスの形式が正しくありません。';
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
        return 'メールアドレスまたはパスワードが違います。';
      case 'auth/user-disabled':
        return 'このアカウントは無効化されています。管理者に連絡してください。';
      case 'auth/too-many-requests':
        return '試行回数が多すぎます。しばらく待ってからお試しください。';
      case 'auth/popup-blocked':
        return 'ログイン用の画面がブラウザにブロックされました。このサイトのポップアップを許可してから、もう一度お試しください。';
      case 'auth/account-exists-with-different-credential':
        return 'このメールアドレスは別の方法で登録されています。管理者に連絡してください。';
      case 'auth/operation-not-allowed':
        return 'Googleアカウントでのログインが有効になっていません。管理者に連絡してください。';
      case 'auth/unauthorized-domain':
        return 'このURLからはログインできません。管理者に連絡してください。';
      case 'auth/network-request-failed':
        return '通信できませんでした。通信状況を確認してください。';
      default:
        return `ログインできませんでした(${e.code})`;
    }
  }
  return 'ログインできませんでした。通信状況を確認してください。';
}

/** 自分で閉じた・二重に開いた は失敗として扱わない */
const isCancel = (e: unknown) =>
  e instanceof FirebaseError && (e.code === 'auth/popup-closed-by-user' || e.code === 'auth/cancelled-popup-request');

export default function LoginPage() {
  const { signIn, signInWithGoogle } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function handleGoogle() {
    setError('');
    setBusy(true);
    try {
      await signInWithGoogle();
      // 遷移は App.tsx のインデックスルートがロール別に振り分ける
    } catch (err) {
      if (!isCancel(err)) setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await signIn(email.trim(), password);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="brand">SC NEXUS / CC DIVISION</div>
        <h1 style={{ marginBottom: 4 }}>cc-system</h1>
        <div className="login-tagline" style={{ marginBottom: 20 }}>
          コールセンター事業部 管理システム
        </div>

        <button
          className="btn-primary"
          type="button"
          onClick={() => void handleGoogle()}
          disabled={busy}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}
        >
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" style={{ background: '#fff', borderRadius: 3, padding: 1 }}>
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
          </svg>
          {busy ? 'ログイン中…' : 'Googleアカウントでログイン'}
        </button>

        {emailLoginAllowed() && (
          <form onSubmit={handleSubmit} style={{ marginTop: 18, paddingTop: 4, borderTop: '1px solid var(--line)' }}>
            <label htmlFor="email">メールアドレス</label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <label htmlFor="password">パスワード</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            <button className="btn-line" type="submit" disabled={busy} style={{ width: '100%', marginTop: 14 }}>
              メールアドレスでログイン
            </button>
          </form>
        )}

        {error && <div className="login-error">{error}</div>}

        <div className="login-note">{BUILD_LABEL}</div>
      </div>
    </div>
  );
}
